import { useState } from 'react';
import { X, ChevronRight, Check, Loader2 } from 'lucide-react';

interface RechargeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRecharge: (data: {
    operator: string;
    operatorName: string;
    phone: string;
    amount: number;
    type: 'AIRTIME' | 'SERVICE';
  }) => void;
}

// ── Datos mock de operadores ──
const MOCK_OPERATORS = [
  { id: 'TELCEL', name: 'Telcel', type: 'AIRTIME' as const, icon: '📱' },
  { id: 'MOVISTAR', name: 'Movistar', type: 'AIRTIME' as const, icon: '📱' },
  { id: 'ATT', name: 'AT&T', type: 'AIRTIME' as const, icon: '📱' },
  { id: 'UNEFON', name: 'Unefon', type: 'AIRTIME' as const, icon: '📱' },
  { id: 'CFE', name: 'CFE', type: 'SERVICE' as const, icon: '💡' },
  { id: 'AGUA', name: 'Agua', type: 'SERVICE' as const, icon: '💧' },
  { id: 'IZZI', name: 'Izzi', type: 'SERVICE' as const, icon: '📺' },
  { id: 'TELMEX', name: 'Telmex', type: 'SERVICE' as const, icon: '📞' },
];

const MOCK_AMOUNTS = [10, 20, 30, 50, 100, 200, 500];

export function RechargeModal({ isOpen, onClose, onRecharge }: RechargeModalProps) {
  const [step, setStep] = useState<'operator' | 'details' | 'confirm'>('operator');
  const [selectedOperator, setSelectedOperator] = useState<typeof MOCK_OPERATORS[0] | null>(null);
  const [phone, setPhone] = useState('');
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [processing, setProcessing] = useState(false);

  if (!isOpen) return null;

  const handleSelectOperator = (op: typeof MOCK_OPERATORS[0]) => {
    setSelectedOperator(op);
    setStep('details');
  };

  const handleSelectAmount = (amount: number) => {
    setSelectedAmount(amount);
    setStep('confirm');
  };

  const handleConfirm = async () => {
    if (!selectedOperator || !selectedAmount) return;
    if (selectedOperator.type === 'AIRTIME' && phone.length !== 10) return;
    setProcessing(true);

    // Simular llamada a Taecel
    await new Promise(resolve => setTimeout(resolve, 1500));

    onRecharge({
      operator: selectedOperator.id,
      operatorName: selectedOperator.name,
      phone,
      amount: selectedAmount,
      type: selectedOperator.type,
    });

    setProcessing(false);
    resetAndClose();
  };

  const resetAndClose = () => {
    setStep('operator');
    setSelectedOperator(null);
    setPhone('');
    setSelectedAmount(null);
    onClose();
  };

  const goBack = () => {
    if (step === 'details') {
      setSelectedOperator(null);
      setStep('operator');
    } else if (step === 'confirm') {
      setSelectedAmount(null);
      setStep('details');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex items-center gap-3">
          {step !== 'operator' && (
            <button onClick={goBack} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
              <ChevronRight size={18} className="rotate-180 text-gray-500" />
            </button>
          )}
          <div className="flex-1">
            <h2 className="text-lg font-black text-gray-800">
              {step === 'operator' ? 'Recargas y Servicios' :
               step === 'details' ? selectedOperator?.name :
               'Confirmar recarga'}
            </h2>
            <p className="text-xs text-gray-500 font-medium">
              {step === 'operator' ? 'Selecciona un operador' :
               step === 'details' ? 'Ingresa los datos' :
               'Verifica antes de vender'}
            </p>
          </div>
          <button onClick={resetAndClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Step 1: Select Operator */}
        {step === 'operator' && (
          <div className="p-5">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Tiempo Aire</p>
            <div className="grid grid-cols-4 gap-3 mb-5">
              {MOCK_OPERATORS.filter(o => o.type === 'AIRTIME').map(op => (
                <button
                  key={op.id}
                  onClick={() => handleSelectOperator(op)}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-all group"
                >
                  <span className="text-2xl">{op.icon}</span>
                  <span className="text-[10px] font-bold text-gray-600 group-hover:text-indigo-600 text-center leading-tight">
                    {op.name}
                  </span>
                </button>
              ))}
            </div>

            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Pago de Servicios</p>
            <div className="grid grid-cols-4 gap-3">
              {MOCK_OPERATORS.filter(o => o.type === 'SERVICE').map(op => (
                <button
                  key={op.id}
                  onClick={() => handleSelectOperator(op)}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-all group"
                >
                  <span className="text-2xl">{op.icon}</span>
                  <span className="text-[10px] font-bold text-gray-600 group-hover:text-indigo-600 text-center leading-tight">
                    {op.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Phone + Amount */}
        {step === 'details' && selectedOperator && (
          <div className="p-5 space-y-5">
            {/* Número telefónico (solo para recargas) */}
            {selectedOperator.type === 'AIRTIME' && (
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                  Número telefónico
                </label>
                <input
                  type="tel"
                  autoFocus
                  maxLength={10}
                  value={phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    if (val.length <= 10) setPhone(val);
                  }}
                  placeholder="55 1234 5678"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all text-lg font-bold text-gray-800 tracking-widest text-center"
                />
                {phone.length > 0 && phone.length < 10 && (
                  <p className="text-xs text-red-500 mt-1">Deben ser 10 dígitos</p>
                )}
              </div>
            )}

            {/* Referencia (para servicios) */}
            {selectedOperator.type === 'SERVICE' && (
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                  Referencia / Código de barras
                </label>
                <input
                  type="text"
                  autoFocus
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Ingresa la referencia"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all text-lg font-bold text-gray-800 text-center"
                />
              </div>
            )}

            {/* Montos */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                Monto
              </label>
              <div className="grid grid-cols-4 gap-2">
                {MOCK_AMOUNTS.map(amount => (
                  <button
                    key={amount}
                    onClick={() => handleSelectAmount(amount)}
                    className="py-3 rounded-xl border-2 font-black text-sm transition-all
                      hover:border-indigo-300 hover:bg-indigo-50
                      text-gray-700 border-gray-200"
                  >
                    ${amount}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 'confirm' && selectedOperator && selectedAmount && (
          <div className="p-5">
            <div className="bg-indigo-50 rounded-xl border border-indigo-200 p-5 text-center mb-5">
              <span className="text-4xl mb-2 block">{selectedOperator.icon}</span>
              <p className="text-lg font-black text-gray-800">{selectedOperator.name}</p>
              {selectedOperator.type === 'AIRTIME' && (
                <p className="text-sm text-gray-500 font-mono mt-1">
                  {phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3')}
                </p>
              )}
            </div>

            <div className="space-y-3 mb-5">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">Monto de recarga</span>
                <span className="font-black text-gray-800">${selectedAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">Comisión estimada</span>
                <span className="font-bold text-green-600">+${(selectedAmount * 0.06).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm font-bold text-gray-700">Total a cobrar</span>
                <span className="text-2xl font-black text-gray-800">${selectedAmount.toFixed(2)}</span>
              </div>
              {selectedOperator.type === 'AIRTIME' && phone.length !== 10 && (
                <p className="text-xs text-red-500 text-center">Ingresa un número telefónico válido de 10 dígitos</p>
              )}
            </div>

            <button
              onClick={handleConfirm}
              disabled={processing || (selectedOperator.type === 'AIRTIME' && phone.length !== 10)}
              className="w-full py-4 rounded-xl font-black text-lg text-white bg-indigo-600 hover:bg-indigo-700 transition-all disabled:bg-gray-200 disabled:text-gray-400 shadow-lg shadow-indigo-500/20"
            >
              {processing ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="animate-spin" size={20} />
                  Procesando...
                </span>
              ) : (
                `VENDER $${selectedAmount.toFixed(2)}`
              )}
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
